/**
 * The cookie choice in the browser: one state every loader and the banner read.
 *
 * `lib/preferences/cookie-consent.ts` owns the model (who is asked, what GPC
 * turns off, where a choice is kept). This module holds the live answer for
 * the current page and tells the loaders when it changes:
 *   - `instrumentation-client.ts` starts PostHog through `whenAnalyticsAllowed`
 *     and samples Sentry's performance data through `analyticsAllowedNow`;
 *   - Google Analytics, New Relic and Dub render through `useCookieConsent`;
 *   - the banner and the settings dialog (`components/privacy/cookie-consent.tsx`)
 *     show the choice and change it.
 *
 * What it reads, in order: the `mt-cookie-consent` choice; the session-only
 * `mt-consent-region=open` marker; `navigator.globalPrivacyControl`. When that
 * is read, it asks `/api/preferences/cookie-consent` for the header-only GPC
 * signal and the current account before enabling trackers. The context is
 * refreshed after each authentication change. Account mirrors carry their
 * owner, so only that account or a genuine anonymous choice can reconcile.
 *
 * Withdrawing consent reloads the page after clearing the analytics cookies
 * and storage: a script that has already run cannot be unloaded, and a reload
 * is the only way to be sure none is left running.
 *
 * Browser-only at run time; importing it on the server is harmless (every
 * entry point checks for `window`).
 */
import {
  COOKIE_CONSENT_COOKIE,
  COOKIE_CONSENT_MAX_AGE_SECONDS,
  CONSENT_REGION_COOKIE,
  CONSENT_REGION_OPEN,
  newerChoice,
  parseCookieConsent,
  parseStoredCookieConsent,
  resolveTrackingPermissions,
  serializeCookieConsent,
  shouldShowConsentBanner,
  type AnalyticsChoice,
  type CookieConsentChoice,
  type TrackingPermissions,
} from '@/lib/preferences/cookie-consent'

export const COOKIE_CONSENT_ENDPOINT = '/api/preferences/cookie-consent'
/** Dispatched on `window` by `openCookieSettings`; the consent component opens its dialog on it. */
export const OPEN_COOKIE_SETTINGS_EVENT = 'mt:open-cookie-settings'

const PUT_TIMEOUT_MS = 4_000

export interface CookieConsentSnapshot {
  /** False until the browser state has been read; nothing renders before that. */
  ready: boolean
  choice: CookieConsentChoice | null
  /** null while unknown. */
  consentRequired: boolean | null
  gpc: boolean
  signedIn: boolean
  /** Consent-only permissions; each loader still applies its own page-scope check. */
  permissions: TrackingPermissions
  /** Before any page-scope check: whether this visitor still has to be asked. */
  needsDecision: boolean
}

interface State {
  ready: boolean
  choice: CookieConsentChoice | null
  consentRequired: boolean | null
  gpc: boolean
  signedIn: boolean
  userId: string | null
  contextReady: boolean
  accountReadable: boolean
  account: CookieConsentChoice | null
  /** Only a decision explicitly made for this identity in this document. Never restored from a cookie. */
  explicitChoice: CookieConsentChoice | null
  /** Whether analytics was allowed at any point in this page's life, so something may be running. */
  analyticsEverAllowed: boolean
  /** Last resolved permissions, retained while authentication/context is uncertain. */
  pendingReconciliationPermissions: TrackingPermissions | null
}

const SERVER_SNAPSHOT: CookieConsentSnapshot = Object.freeze({
  ready: false,
  choice: null,
  consentRequired: null,
  gpc: false,
  signedIn: false,
  permissions: Object.freeze({ analytics: false, saleOrSharing: false }),
  needsDecision: false,
}) as CookieConsentSnapshot

let state: State = freshState()
let snapshot: CookieConsentSnapshot = SERVER_SNAPSHOT
const listeners = new Set<() => void>()
let analyticsWaiters: Array<(allowed: boolean) => void> = []
let contextRequest: Promise<void> | null = null
let contextRetry: ReturnType<typeof setTimeout> | null = null
let authIdentity: string | null | undefined
let contextGeneration = 0
let consentChannel: BroadcastChannel | null = null
let notificationsStarted = false
let reloadPage: () => void = () => window.location.reload()
let withdrawalReloaded = false

function freshState(): State {
  return {
    ready: false,
    choice: null,
    consentRequired: null,
    gpc: false,
    signedIn: false,
    userId: null,
    contextReady: false,
    accountReadable: false,
    account: null,
    explicitChoice: null,
    analyticsEverAllowed: false,
    pendingReconciliationPermissions: null,
  }
}

function inBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function readCookie(name: string): string | null {
  try {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  } catch {
    return null
  }
}

function writeChoiceCookie(choice: CookieConsentChoice): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${COOKIE_CONSENT_COOKIE}=${serializeCookieConsent(choice, choice.userId ?? null)}; Path=/; Max-Age=${COOKIE_CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
  } catch {
    // A browser that refuses cookies keeps the choice for this page only.
  }
}

function browserGpc(): boolean {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  } catch {
    return false
  }
}

function hasExplicitChoice(s: State): boolean {
  return !!s.choice && s.choice.userId === s.userId && s.explicitChoice?.userId === s.userId &&
    s.explicitChoice.analytics === s.choice.analytics && s.explicitChoice.decidedAt === s.choice.decidedAt
}

function permissionsOf(s: State): TrackingPermissions {
  // Cached cookies cannot answer Sec-GPC or identify the current account.
  if (!s.contextReady || (s.signedIn && !s.accountReadable && !hasExplicitChoice(s))) return { analytics: false, saleOrSharing: false }
  return resolveTrackingPermissions({ surface: true, choice: s.choice, consentRequired: s.consentRequired, gpc: s.gpc })
}

function rebuildSnapshot(): void {
  snapshot = {
    ready: state.ready,
    choice: state.choice,
    consentRequired: state.consentRequired,
    gpc: state.gpc,
    signedIn: state.signedIn,
    permissions: permissionsOf(state),
    needsDecision: shouldShowConsentBanner({
      surface: true,
      choice: state.choice,
      consentRequired: state.consentRequired,
      gpc: state.gpc,
    }),
  }
}

/** Publish the new state; a withdrawal clears analytics storage and reloads. */
function commit(next: State, deferReload = false): boolean {
  const confirmed = (next.contextReady && (!next.signedIn || next.accountReadable || hasExplicitChoice(next))) || next.choice?.analytics === 'denied'
  const before = state.pendingReconciliationPermissions ?? permissionsOf(state)
  const after = permissionsOf(next)
  const everAllowed = state.analyticsEverAllowed || after.analytics
  // An explicit refusal is a decision even while GET is pending. An unreadable
  // account is still uncertain, but a confirmed GPC signal already forbids sharing.
  const needsReload = everAllowed && ((confirmed && before.analytics && !after.analytics) ||
    (before.saleOrSharing && ((confirmed && !after.saleOrSharing) || (next.contextReady && next.gpc))))
  state = { ...next, analyticsEverAllowed: everAllowed, pendingReconciliationPermissions: confirmed ? null : next.pendingReconciliationPermissions }
  rebuildSnapshot()

  if (needsReload || (!after.analytics && state.choice?.analytics === 'denied')) clearAnalyticsStorage()

  if (after.analytics) {
    const waiting = analyticsWaiters
    analyticsWaiters = []
    for (const resolve of waiting) resolve(true)
  }
  for (const listener of [...listeners]) listener()
  if (needsReload && !deferReload) reloadAfterWithdrawal()
  return needsReload
}

function reloadAfterWithdrawal(): void {
  if (withdrawalReloaded) return
  withdrawalReloaded = true
  try {
    reloadPage()
  } catch {
    // Locked-down frames: subscribers and the live permissions still stop work.
  }
}

/** Read the browser's own state once. Idempotent; a no-op outside a browser. */
export function initCookieConsent(): void {
  if (!inBrowser() || state.ready) return
  startConsentNotifications()
  const choice = parseCookieConsent(readCookie(COOKIE_CONSENT_COOKIE))
  const regionOpen = readCookie(CONSENT_REGION_COOKIE) === CONSENT_REGION_OPEN
  commit({
    ...state,
    ready: true,
    choice,
    consentRequired: regionOpen ? false : null,
    gpc: browserGpc(),
  })
}

/** Notifications contain decisions only; opening a page writes no storage. */
function publishChoice(choice: CookieConsentChoice): void {
  try {
    consentChannel?.postMessage({ choice })
  } catch {
    // Focus/pageshow also reconcile the shared cookie when channels are unavailable.
  }
}

function receiveChoice(raw: unknown): void {
  if (!state.ready || !raw || typeof raw !== 'object') return
  const record = raw as CookieConsentChoice
  if (record.userId !== null && (typeof record.userId !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(record.userId))) return
  const parsed = parseStoredCookieConsent({ analytics: record.analytics, decided_at: record.decidedAt })
  if (!parsed) return
  // An account mirror belongs only to that identity. Anonymous decisions may
  // reconcile only in an anonymous document, never through another account.
  if (record.userId !== state.userId) return
  const incoming = { ...parsed, userId: record.userId }
  if (newerChoice(state.choice, incoming) !== incoming) return
  writeChoiceCookie(incoming)
  commit({ ...state, choice: incoming })
}

function reconcileCookie(): void {
  receiveChoice(parseCookieConsent(readCookie(COOKIE_CONSENT_COOKIE)))
  if (!state.contextReady || (state.signedIn && !state.accountReadable)) void ensureCookieConsent()
}

function startConsentNotifications(): void {
  if (notificationsStarted) return
  notificationsStarted = true
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      consentChannel = new BroadcastChannel('mt-cookie-consent')
      consentChannel.onmessage = (event: MessageEvent) => receiveChoice(event.data?.choice)
    }
  } catch {
    // A blocked channel does not prevent local decisions or cookie reconciliation.
  }
  window.addEventListener('focus', reconcileCookie)
  window.addEventListener('pageshow', reconcileCookie)
}

interface ContextResponse {
  consentRequired?: unknown
  gpc?: unknown
  signedIn?: unknown
  userId?: unknown
  account?: unknown
  accountReadable?: unknown
}

async function putAccountChoice(choice: CookieConsentChoice, userId: string, generation = contextGeneration): Promise<void> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), PUT_TIMEOUT_MS) : null
  try {
    const res = await fetch(COOKIE_CONSENT_ENDPOINT, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ analytics: choice.analytics, decidedAt: choice.decidedAt, userId }),
      signal: controller?.signal,
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const body = await res.json()
    const winner = parseStoredCookieConsent({ analytics: body?.choice?.analytics, decided_at: body?.choice?.decidedAt })
    if (!winner) throw new Error('Invalid saved cookie choice')
    if (generation !== contextGeneration || state.userId !== userId || (state.choice?.analytics !== choice.analytics || state.choice?.decidedAt !== choice.decidedAt || state.choice?.userId !== choice.userId)) return
    if (winner.analytics === choice.analytics && winner.decidedAt === choice.decidedAt) return
    const persisted = { ...winner, userId }
    writeChoiceCookie(persisted)
    commit({ ...state, choice: persisted, account: persisted })
    publishChoice(persisted)
  } catch (error) {
    // The browser keeps the choice; the next signed-in page load retries.
    console.warn('[cookie-consent] could not save the choice to the account:', error instanceof Error ? error.message : error)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

async function fetchContext(generation: number): Promise<boolean> {
  let body: ContextResponse
  try {
    const res = await fetch(COOKIE_CONSENT_ENDPOINT, { credentials: 'same-origin', cache: 'no-store' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    body = (await res.json()) as ContextResponse
    if (!body || typeof body.consentRequired !== 'boolean' || typeof body.signedIn !== 'boolean' ||
      (body.signedIn && (typeof body.userId !== 'string' || !body.userId))) throw new Error('Invalid consent context')
  } catch (error) {
    // Unknown stays unknown, which asks: nothing non-essential loads.
    console.warn('[cookie-consent] could not load the consent context:', error instanceof Error ? error.message : error)
    if (generation === contextGeneration) commit({ ...state, consentRequired: true })
    return false
  }

  if (generation !== contextGeneration) return false

  const stored = body.account && typeof body.account === 'object' ? (body.account as { analytics?: unknown; decidedAt?: unknown }) : null
  const account = stored ? parseStoredCookieConsent({ analytics: stored.analytics, decided_at: stored.decidedAt }) : null
  const userId = body.signedIn === true && typeof body.userId === 'string' ? body.userId : null
  const signedIn = userId !== null
  const accountReadable = body.accountReadable === true
  // Legacy cookies have no provenance. Keep them locally for visitors, but
  // never promote them (or another account's mirror) into a signed-in account.
  let choice = state.choice
  let explicitChoice = state.explicitChoice
  if (signedIn ? choice?.userId !== null && choice?.userId !== userId : choice?.userId != null) choice = null
  if (signedIn && choice?.userId === null) {
    // Claim an anonymous choice even when this account cannot be read yet;
    // a subsequent account must not inherit it during a transient read failure.
    choice = { ...choice, userId }
    explicitChoice = null
    writeChoiceCookie(choice)
  }
  if (signedIn && accountReadable) {
    const winner = newerChoice(account, choice)
    if (winner) {
      choice = { ...winner, userId }
      writeChoiceCookie(choice)
    }
  }
  if (!choice && state.choice) clearChoiceCookie()
  commit({
    ...state,
    choice,
    consentRequired: typeof body.consentRequired === 'boolean' ? body.consentRequired : true,
    gpc: state.gpc || body.gpc === true,
    signedIn,
    userId,
    contextReady: true,
    accountReadable,
    account,
    explicitChoice,
  })
  if (signedIn && accountReadable && choice && (!account || choice.decidedAt > account.decidedAt)) {
    void putAccountChoice(choice, userId!, generation)
  }
  return !signedIn || accountReadable
}

/**
 * Make sure the state is complete: read the browser and ask the endpoint,
 * checking the request-header GPC signal even with cached cookies. One request per identity.
 */
export function ensureCookieConsent(): Promise<void> {
  if (!inBrowser()) return Promise.resolve()
  initCookieConsent()
  if (contextRequest) return contextRequest
  const generation = contextGeneration
  const request = fetchContext(generation).then((resolved) => {
    if (generation !== contextGeneration || contextRequest !== request) return
    if (contextRetry) clearTimeout(contextRetry)
    contextRetry = null
    if (!resolved) {
      contextRequest = null
      contextRetry = setTimeout(() => {
        contextRetry = null
        void ensureCookieConsent()
      }, PUT_TIMEOUT_MS)
    }
  })
  contextRequest = request
  return request
}

/** Reconcile again after sign-in, sign-out, or an account switch in this page. */
export function syncCookieConsentWithAccount(userId: string | null): Promise<void> {
  if (!inBrowser()) return Promise.resolve()
  initCookieConsent()
  if (authIdentity !== userId) {
    const previous = authIdentity === undefined ? state.userId : authIdentity
    authIdentity = userId
    if (previous !== userId) {
      contextGeneration++
      if (contextRetry) clearTimeout(contextRetry)
      contextRetry = null
      contextRequest = null
      let choice = state.choice
      if (choice?.userId !== null && choice?.userId !== userId) {
        choice = null
        clearChoiceCookie()
      }
      // Stop using the previous person's context while the new one loads.
      commit({ ...state, choice, userId, signedIn: userId !== null, account: null, explicitChoice: null, accountReadable: false, contextReady: false,
        pendingReconciliationPermissions: state.pendingReconciliationPermissions ?? permissionsOf(state) })
    }
  }
  return ensureCookieConsent()
}

function clearChoiceCookie(): void {
  try {
    document.cookie = `${COOKIE_CONSENT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
  } catch {
    // Cookies may be blocked; the in-memory choice is still cleared.
  }
}

/** True once analytics may run in this browser, by consent alone. */
export function analyticsAllowedNow(): boolean {
  return permissionsOf(state).analytics
}

/** Resolves true when analytics becomes allowed on this page; stays pending while it is not. */
export function whenAnalyticsAllowed(): Promise<boolean> {
  if (!inBrowser()) return new Promise<boolean>(() => {})
  void ensureCookieConsent()
  if (analyticsAllowedNow()) return Promise.resolve(true)
  return new Promise<boolean>((resolve) => {
    analyticsWaiters.push(resolve)
  })
}

/** Publish the choice immediately; only a withdrawal's reload waits for the bounded account save. */
export async function setCookieChoice(analytics: AnalyticsChoice, options: { userId?: string | null }): Promise<void> {
  if (!inBrowser()) return
  initCookieConsent()
  const userId = options.userId === undefined ? state.userId : options.userId
  const generation = contextGeneration
  const choice: CookieConsentChoice = { analytics, decidedAt: Math.max(Date.now(), (state.choice?.decidedAt ?? 0) + 1), userId }
  writeChoiceCookie(choice)
  const needsReload = commit({ ...state, choice, explicitChoice: choice, account: userId ? choice : state.account }, userId !== null)
  publishChoice(choice)
  if (!state.contextReady || (state.signedIn && !state.accountReadable)) void ensureCookieConsent()
  if (userId !== null) await putAccountChoice(choice, userId, generation)
  if (needsReload && userId !== null && generation === contextGeneration && state.choice?.analytics === 'denied') reloadAfterWithdrawal()
}

export function subscribeCookieConsent(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getCookieConsentSnapshot(): CookieConsentSnapshot {
  return snapshot
}

export function getServerCookieConsentSnapshot(): CookieConsentSnapshot {
  return SERVER_SNAPSHOT
}

/** Open the settings dialog from anywhere (the site footer, the Cookie Policy). */
export function openCookieSettings(): void {
  if (!inBrowser()) return
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT))
}

/** Cookie and storage names the analytics tools write. Nothing of the app's own. */
const ANALYTICS_COOKIE = /^(_ga($|_)|_gid$|_gat|ph_|dub_id$|dub_partner_data$|NREUM|NRAGENT|NRBA_)/
const ANALYTICS_STORAGE_KEY = /^(ph_|__ph|NRBA)/

/** The host and every parent domain a script could have scoped a cookie to. */
function cookieDomains(hostname: string): string[] {
  const labels = hostname.split('.')
  const domains = ['']
  for (let i = 0; i < labels.length - 1; i++) domains.push(`; Domain=.${labels.slice(i).join('.')}`)
  return domains
}

/** Delete what the analytics tools stored in this browser. Best effort; never throws. */
export function clearAnalyticsStorage(): void {
  if (!inBrowser()) return
  try {
    const names = document.cookie
      .split(';')
      .map((part) => part.split('=')[0]?.trim())
      .filter((name): name is string => !!name && ANALYTICS_COOKIE.test(name))
    const domains = cookieDomains(window.location.hostname)
    for (const name of names) {
      for (const domain of domains) document.cookie = `${name}=; Path=/; Max-Age=0${domain}`
    }
  } catch {
    // Cookies unavailable: nothing to clear.
  }
  for (const storage of ['localStorage', 'sessionStorage'] as const) {
    try {
      const store = window[storage]
      const keys: string[] = []
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i)
        if (key && ANALYTICS_STORAGE_KEY.test(key)) keys.push(key)
      }
      for (const key of keys) store.removeItem(key)
    } catch {
      // Storage blocked: nothing was written there either.
    }
  }
}

/** Tests only: forget everything this module learned (and stand in for the page reload). */
export function __resetCookieConsentForTests(options: { reload?: () => void } = {}): void {
  withdrawalReloaded = false
  if (contextRetry) clearTimeout(contextRetry)
  contextRetry = null
  consentChannel?.close()
  consentChannel = null
  notificationsStarted = false
  if (inBrowser()) {
    window.removeEventListener('focus', reconcileCookie)
    window.removeEventListener('pageshow', reconcileCookie)
  }
  reloadPage = options.reload ?? (() => window.location.reload())
  state = freshState()
  snapshot = SERVER_SNAPSHOT
  listeners.clear()
  analyticsWaiters = []
  contextRequest = null
  authIdentity = undefined
  contextGeneration++
}
